import { Box, SimplePaletteColorOptions, Tooltip, Typography, useTheme } from "@mui/material";
import { TextOverflow, formatNumber, formatPercent } from "vig-ui-library";

export type SliderChartItemProps = {
    percentage: number;
    color?: SimplePaletteColorOptions;
    sliderHeight: number;
    sliderPadding: number;
    icon?: React.ReactNode;
    label: string;
    count: number;
}

export type SliderChartData = Omit<SliderChartItemProps, "sliderHeight" | "sliderPadding">;

export type SliderChartProps = {
    sliderHeight?: number;
    sliderPadding?: number;
    dataset: SliderChartData[];
}

export const SliderChart = (props: SliderChartProps) => {
    const theme = useTheme();

    const sliderHeight = props.sliderHeight || 50;

    const sliderPadding = props.sliderPadding || 1;

    return (
        <Box sx={{
            height: sliderHeight,
            width: "100%",
            borderRadius: `${sliderHeight}px`,
            flexShrink: 0,
            backgroundColor: theme.palette.grey[100],
            padding: sliderPadding,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
        }}>
            {props.dataset.map((item, index) => (
                <SliderChartItem
                    key={index}
                    {...item}
                    sliderHeight={sliderHeight}
                    sliderPadding={sliderPadding}
                />
            ))}
        </Box>
    );
}

export const SliderChartItem = (props: SliderChartItemProps) => {
    const theme = useTheme();

    const {
        percentage,
        sliderHeight,
        sliderPadding,
    } = props;

    const itemHeight = sliderHeight - ((theme.variables.spacing * sliderPadding) * 2);

    const verticalCenter = {
        display: "flex",
        alignItems: "center",
        gap: 1,
        overflow: "hidden",
    }

    const backgroundColor = props.color?.main || "transparent";

    const labelColor = props.color?.contrastText || theme.palette.text.secondary;

    const countColor = props.color?.contrastText || theme.palette.text.primary;

    return (
        <Box sx={{
            height: itemHeight,
            width: `${percentage}%`,
            borderRadius: `${itemHeight}px`,
            backgroundColor: backgroundColor,
            paddingY: 0.5,
            paddingX: 1,
            ...verticalCenter,
            justifyContent: "space-between",
            gap: 2,
            minWidth: 180,
        }}>
            <Box sx={{
                ...verticalCenter,
                flexGrow: 1,
            }}>
                {props.icon && (
                    <Box flexShrink={0} width={24} display="flex" alignItems="center">
                        {props.icon}
                    </Box>
                )}

                <Tooltip title={props.label}>
                    <Box sx={{ flexGrow: 1, width: "100%", display: "flex", alignItems: "center", overflow: "hidden" }}>
                        <TextOverflow maxWidth="100%" variant="body2" color={labelColor} text={props.label} />
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{
                ...verticalCenter,
                flexShrink: 0,
            }}>
                <Typography variant="body1" color={countColor} fontWeight={500}>
                    {formatNumber(props.count)}x
                </Typography>

                <Box sx={{ height: "100%", borderRadius: "50px", paddingX: 1, display: "flex", alignItems: "center", background: theme.palette.common.white }}>
                    <Typography variant="body2" fontSize={14} color={theme.palette.text.primary}>
                        {formatPercent(percentage / 100, 0)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}